import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Home, 
  Search, 
  MapPin, 
  Phone, 
  Mail, 
  Star,
  Building2,
  Users,
  TrendingUp,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RealtorsPage({ user, subscription, onLogout }) {
  const navigate = useNavigate();
  const [realtors, setRealtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const { mainClass } = useSidebar();

  useEffect(() => {
    fetchRealtors();
  }, []);

  const fetchRealtors = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/realtor/directory`, {
        withCredentials: true
      });
      setRealtors(response.data || []);
    } catch (error) {
      console.error("Failed to fetch realtors:", error);
      // Show sample data for now
      setRealtors([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredRealtors = realtors.filter(realtor => {
    const matchesSearch = !searchQuery || 
      realtor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      realtor.company?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCity = !cityFilter || 
      realtor.city?.toLowerCase().includes(cityFilter.toLowerCase());
    return matchesSearch && matchesCity;
  });

  return (
    <div className="min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass}>
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white flex items-center gap-3">
                <Home className="w-8 h-8 text-gold" />
                Realtor Network
              </h1>
              <p className="text-white/50 mt-1">Find and connect with real estate agents in your market</p>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <Input
                  placeholder="Search by name or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-black border-white/[0.08] text-white"
                />
              </div>
              <div className="w-full md:w-64 relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <Input
                  placeholder="Filter by city..."
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="pl-10 bg-black border-white/[0.08] text-white"
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
                <div>
                  <p className="text-2xl font-bold text-white">{realtors.length}</p>
                  <p className="text-sm text-white/50">Realtors in Network</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]" />
                <div>
                  <p className="text-2xl font-bold text-white">
                    {new Set(realtors.map(r => r.company)).size}
                  </p>
                  <p className="text-sm text-white/50">Brokerages</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-white">
                    {new Set(realtors.map(r => r.city)).size}
                  </p>
                  <p className="text-sm text-white/50">Markets Covered</p>
                </div>
              </div>
            </div>
          </div>

          {/* Realtors Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredRealtors.length === 0 ? (
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
              <Home className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Realtors Found</h3>
              <p className="text-white/50 mb-6">
                {realtors.length === 0 
                  ? "Be the first to build your realtor network! Invite realtors to join the platform."
                  : "No realtors match your search criteria. Try adjusting your filters."}
              </p>
              <Button 
                className="gold-gradient-btn"
                onClick={() => toast.info("Invite feature coming soon!")}
              >
                Invite a Realtor
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRealtors.map((realtor) => (
                <div 
                  key={realtor.realtor_id || realtor.id}
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-gold/50 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                      {realtor.name?.charAt(0) || "R"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white truncate">{realtor.name}</h3>
                      <p className="text-sm text-white/50 truncate">{realtor.company || "Independent"}</p>
                      {realtor.city && (
                        <p className="text-xs text-white/40 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {realtor.city}, {realtor.state}
                        </p>
                      )}
                    </div>
                    {realtor.rating && (
                      <div className="flex items-center gap-1 text-gold">
                        <Star className="w-4 h-4 fill-gold" />
                        <span className="text-sm font-medium">{realtor.rating}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-white/[0.08] flex items-center gap-2">
                    {realtor.phone && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 border-white/[0.1] text-white/70 hover:text-white hover:border-white/[0.15]"
                        onClick={() => window.open(`tel:${realtor.phone}`)}
                      >
                        <Phone className="w-4 h-4 mr-1" />
                        Call
                      </Button>
                    )}
                    {realtor.email && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 border-white/[0.1] text-white/70 hover:text-white hover:border-white/[0.15]"
                        onClick={() => window.open(`mailto:${realtor.email}`)}
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Email
                      </Button>
                    )}
                    <Button 
                      size="sm"
                      className="flex-1 bg-gold/20 text-gold hover:bg-gold/30 border border-gold/30"
                      onClick={() => navigate(`/realtors/${realtor.realtor_id || realtor.id}`)}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
